import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';
import { toast } from 'react-toastify';
import _classNames from 'classnames';
import { Box, Button, Grid, Typography, makeStyles } from '@material-ui/core';
import { sharedStyles } from 'containers/Series/components/seriesSharedStyles';
import AssessmentItemSmall from 'components/AssessmentItemSmall';
import { getResourceName, getResourceType } from 'containers/Series/utils';
import { getTakenAssessments } from 'containers/Assessments/actions';
import Mixpanel from 'utils/mixpanelService';
import { RecommendedResource } from './RecommendedResource';
import { getCourseResourcesSaveForLaterProcessing } from '../selectors';
import RecommendedBooksPreview from './recommended-resources-preview/RecommendedBooksPreview';
import { splitRecommendedResources } from '../utils';

const useStyles = makeStyles(theme => ({
  button: {
    alignSelf: 'flex-start',
    flexShrink: 0,
    borderRadius: 20,
  },
  disabledButton: {
    pointerEvents: 'none !important',
  },
  title: {
    marginRight: theme.spacing(1),
  },
  recommendedContainer: {
    display: 'grid',
    gridTemplateColumns:
      // display 2 columns if width more than 330px, otherwise 1 column
      'repeat(auto-fit, minmax(max(calc(50% - 12px), 330px), 1fr))',
    columnGap: theme.spacing(3),
  },
  ...sharedStyles(theme),
}));

export const CourseRecommended = ({
  course,
  series,
  coursesSiteCopy,
  seriesSiteCopy,
  onNext,
  saveResourceForLater,
  takenCourse,
  trackingProps,
  small,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const courseResourcesSaveForLaterProcessingList = useSelector(
    getCourseResourcesSaveForLaterProcessing,
  );

  useEffect(() => {
    dispatch(getTakenAssessments());
  }, []);

  const savedResourceIds = _get(takenCourse, 'savedResourceIds', []);

  const handleSaveForLater = (resource, isBook) => {
    const resourceId = resource.sys.id;
    saveResourceForLater({
      resourceId,
    });

    Mixpanel.track('Course - Recommended Resource - Saved', {
      ...trackingProps,
      type: getResourceType(resource),
      name: getResourceName(resource),
    });

    const toastMessageProperty = isBook
      ? 'bookSavedSuccessMessage'
      : 'otherResourcesSavedSuccessMessage';
    toast(_get(coursesSiteCopy, ['pageCopy', toastMessageProperty]), {
      type: 'success',
    });
  };

  const getDefaultSaveForLaterButton = ({ isDisabled, resource, isSaved }) => {
    const buttonTextProperty = isSaved ? 'savedForLater' : 'saveForLater';

    return (
      <Button
        className={_classNames(classes.button, {
          [classes.disabledButton]: isDisabled,
        })}
        variant="outlined"
        color="primary"
        onClick={() => handleSaveForLater(resource)}
        disabled={isDisabled}
      >
        {_get(coursesSiteCopy, `pageCopy.${buttonTextProperty}`)}
      </Button>
    );
  };

  const renderSaveForLaterButton = (
    item,
    getSaveForLaterButton = getDefaultSaveForLaterButton,
  ) => {
    const resourceId = _get(item, 'sys.id');
    const isSaved = savedResourceIds.includes(resourceId);

    const isResourceLoading = (
      courseResourcesSaveForLaterProcessingList || []
    ).includes(resourceId);

    const isDisabled = isSaved || isResourceLoading;

    return getSaveForLaterButton({
      isDisabled,
      resource: item,
      isSaved,
      onClick: handleSaveForLater,
    });
  };

  const openAssessment = redirectUrl => {
    window.open(redirectUrl, '_blank');
  };
  const resources = _get(series, 'fields.recommendedResources');
  const {
    rest: restResources,
    book: bookResources,
    assessments,
  } = splitRecommendedResources(resources);

  return (
    <div>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography
          className={classes.title}
          color="textPrimary"
          variant={small ? 'h6' : 'h5'}
          gutterBottom
        >
          {_get(coursesSiteCopy, 'pageCopy.recommendedResources')}
        </Typography>
        <Button
          className={classes.nextButton}
          variant="contained"
          color="primary"
          onClick={() => onNext()}
        >
          {_get(seriesSiteCopy, 'pageCopy.next')}
        </Button>
      </Box>
      <Typography color="textPrimary" variant="body2">
        {_get(coursesSiteCopy, 'pageCopy.recommendedResourcesDescription')}
      </Typography>
      <Box className={classes.recommendedContainer}>
        {restResources.map(item => (
          <RecommendedResource
            key={_get(item, 'sys.id')}
            course={course}
            item={item}
            coursesSiteCopy={coursesSiteCopy}
            saveForLaterButton={renderSaveForLaterButton(item)}
            trackingProps={trackingProps}
            small={small}
          />
        ))}
      </Box>
      {!!assessments.length && (
        <Box mt={small ? 2 : 4}>
          <Grid container spacing={2}>
            {assessments.map(item => (
              <Grid item lg={3} md={4} sm={4} xs={12} key={item.sys.id}>
                <AssessmentItemSmall
                  item={{
                    id: item.sys.id,
                    ...item.fields,
                  }}
                  onRedirect={openAssessment}
                  trackingProps={trackingProps}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      {!!bookResources.length && (
        <Box mt={small ? 2 : 4}>
          <Box mb={3}>
            <Typography color="textPrimary" variant="h5" gutterBottom>
              {_get(coursesSiteCopy, 'pageCopy.recommendedBooks')}
            </Typography>
          </Box>
          <RecommendedBooksPreview
            items={bookResources}
            coursesSiteCopy={coursesSiteCopy}
            renderButton={renderSaveForLaterButton}
          />
        </Box>
      )}
    </div>
  );
};
